import type { DevAutoCalcLogDto } from '@/api';
import FilterDropdown from '@/components/table/toolbar/filter-dropdown';
import { formatDateTimeDisplay } from '@/utils/date-utils';
import { Dropdown, Label } from '@lego/connect-components-react';
import { Box, Flex, Text } from '@lego/klik-ui';
import { ThemeProvider, createTheme } from '@mui/material';
import {
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  MRT_ToggleFiltersButton,
  MaterialReactTable,
} from 'material-react-table';
import { useMemo, useState } from 'react';
import {
  type AutoCalcFilterColumn,
  countAutoCalcFilters,
  getAutoCalcColumnOptions,
  resolveSelectedRun,
  toAutoCalcLogFilter,
  toRunOptions,
} from '../autocalc-log-filters';
import {
  useDevAutoCalcLogs,
  useDevAutoCalcRuns,
} from '../dev-dashboard-queries';
import { ActiveFilterStatus } from './active-filter-status';

// A type alias (not an interface) so it fits the Record<string, unknown> row type of material-react-table helpers.
type LogRow = Pick<DevAutoCalcLogDto, keyof DevAutoCalcLogDto>;

const theme = createTheme({ palette: { mode: 'light' } });

const cellText = (text: string | number) => (
  <Text className="body-xs-regular" isTruncated>
    {text}
  </Text>
);

/**
 * AutoCalc fail logs. A run is always selected (the newest by default), and the table shows all logs of
 * that run. The column filters run in the backend, within the selected run.
 */
export const AutoCalcLogsPanel = () => {
  const runs = useDevAutoCalcRuns();
  const [chosenRunKey, setChosenRunKey] = useState<string>();
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    [],
  );

  const runOptions = useMemo(() => toRunOptions(runs.data ?? []), [runs.data]);
  const runKey = resolveSelectedRun(runs.data ?? [], chosenRunKey);

  // All logs of the run give the filter options; the filtered logs fill the table.
  const runLogs = useDevAutoCalcLogs(runKey ? { runKey } : undefined);
  const filter = runKey
    ? toAutoCalcLogFilter(runKey, columnFilters)
    : undefined;
  const filteredLogs = useDevAutoCalcLogs(filter);
  const rows: LogRow[] = filteredLogs.data ?? [];

  const selectRun = (key: string) => {
    setChosenRunKey(key);
    // The filter values of one run do not apply to another run.
    setColumnFilters([]);
  };

  const filterColumn = (
    key: AutoCalcFilterColumn,
    header: string,
    size: number,
  ): MRT_ColumnDef<LogRow> => ({
    accessorKey: key,
    header,
    size,
    Cell: ({ row }) => cellText(row.original[key] ?? ''),
    Filter: ({ header: columnHeader }) => (
      <FilterDropdown
        options={getAutoCalcColumnOptions(key, runLogs.data ?? [])}
        value={columnHeader.column.getFilterValue() as string[] | undefined}
        onChange={(selected) =>
          columnHeader.column.setFilterValue(
            selected.length > 0 ? selected : undefined,
          )
        }
      />
    ),
  });

  const columns: MRT_ColumnDef<LogRow>[] = [
    {
      id: 'runDate',
      accessorFn: (row) => row.runDate,
      header: 'Run',
      size: 140,
      enableColumnFilter: false,
      enableSorting: false,
      Cell: ({ row }) => cellText(formatDateTimeDisplay(row.original.runDate)),
    },
    filterColumn('priceUnitId', 'Price List', 120),
    filterColumn('productId', 'Product', 120),
    filterColumn('category', 'Category', 200),
    {
      accessorKey: 'detail',
      header: 'Detail',
      size: 200,
      enableColumnFilter: false,
      Cell: ({ row }) => (
        <span className="body-xs-regular" style={{ whiteSpace: 'normal' }}>
          {row.original.detail ?? ''}
        </span>
      ),
    },
    {
      ...filterColumn('reason', 'Error', 400),
      grow: true,
      Cell: ({ row }) => (
        <Text className="body-xs-regular" whiteSpace="normal">
          {row.original.reason}
        </Text>
      ),
    },
  ];

  if (runs.isPending) return <Text>Loading runs…</Text>;
  if (runs.isError) {
    return <Text>Could not load the runs. They reload automatically.</Text>;
  }
  if (!runKey) return <Text>No AutoCalc fail logs.</Text>;

  return (
    <Flex flexDirection="column" gap={3} flex={1} minHeight={0}>
      <Box width="280px">
        <Label>Run</Label>
        <Dropdown
          defaultValue={[runKey]}
          onChange={(_, value) => selectRun(value[0]?.value as string)}
        >
          {runOptions.map((option) => (
            <Dropdown.Option key={option.id} value={option.id}>
              {option.label}
            </Dropdown.Option>
          ))}
        </Dropdown>
      </Box>
      <Box flex={1} minHeight={0} display="flex" flexDirection="column">
        <ThemeProvider theme={theme}>
          <MaterialReactTable
            columns={columns}
            data={rows}
            getRowId={(row) => String(row.id)}
            manualFiltering
            onColumnFiltersChange={setColumnFilters}
            state={{
              columnFilters,
              showProgressBars: filteredLogs.isFetching,
            }}
            enableSorting
            enableStickyHeader
            enablePagination={false}
            enableColumnActions={false}
            enableGlobalFilter={false}
            enableBottomToolbar={false}
            layoutMode="grid"
            initialState={{ density: 'compact', showColumnFilters: true }}
            muiTablePaperProps={{
              elevation: 0,
              sx: {
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              },
            }}
            muiTableContainerProps={{ sx: { flex: 1, minHeight: 0 } }}
            muiTableHeadCellProps={{
              sx: { fontSize: '11px', fontWeight: 600 },
            }}
            renderEmptyRowsFallback={() => (
              <Text className="body-xs-regular" padding={2}>
                {filteredLogs.isError
                  ? 'Could not load the data. It reloads automatically.'
                  : 'No logs match the filters.'}
              </Text>
            )}
            renderTopToolbar={({ table }) => (
              <Flex marginY="4px" gap="8px" alignItems="center">
                <MRT_ToggleFiltersButton table={table} size="small" />
                <ActiveFilterStatus
                  count={filter ? countAutoCalcFilters(filter) : 0}
                  onClear={() => table.resetColumnFilters()}
                />
                <Text className="body-xs-regular" marginLeft="auto">
                  {rows.length.toLocaleString('en-GB')} logs
                </Text>
              </Flex>
            )}
          />
        </ThemeProvider>
      </Box>
    </Flex>
  );
};
